/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Wheel-driven scrolling for a screen that owns its own scrollport.
 *
 * A browser scrolls an `overflow-y: auto` box for free, so this looks like work
 * the platform already does. It is not: `NavigationEngine` listens for `wheel`
 * on the document in the capture phase, non-passive, and calls
 * `preventDefault()` on every non-zero delta — see
 * `PointerMapper.resolveWheelEvent`, which resolves any `deltaY` to
 * SCROLL_UP/SCROLL_DOWN. That is deliberate: the wheel is an input action for
 * the screens that consume it as one, driving D-pad-style navigation with a
 * mouse. The cost is that no screen in the app has native scrolling left to
 * inherit, so one that wants the wheel to *scroll* has to move its scrollport
 * itself.
 *
 * Scrolling only, deliberately: this moves the viewport and nothing else. A
 * screen whose wheel also steps the cursor has made scrolling and highlighting
 * one gesture, which puts the highlight — and anything a highlight reveals,
 * like a card's action row — on whatever the wheel reached rather than on what
 * the pointer is over. Let hover pick the item and let this decide what is on
 * screen.
 *
 * Browser-only in practice: a TV has no wheel, and there the D-pad moves focus
 * with the viewport following it, so a wheel that moved the page on its own
 * would only put the two out of step. The caller decides — the usual shape is
 * `onWheel={isSmartTV ? undefined : handleWheel}`.
 */

/**
 * Pixels one wheel notch is worth where a browser reports its delta in lines.
 * Matches the step Blink itself uses for that conversion.
 */
const WHEEL_LINE_HEIGHT_PX: number = 40;

/** `WheelEvent.deltaMode`: the delta counts lines, not pixels. */
const DELTA_MODE_LINE: number = 1;

/** `WheelEvent.deltaMode`: the delta counts pages, not pixels. */
const DELTA_MODE_PAGE: number = 2;

/**
 * The part of a wheel event this needs.
 *
 * Structural rather than the DOM's `WheelEvent`, so React's synthetic event
 * satisfies it without a cast — call sites have one or the other.
 */
export interface IWheelDelta {
  deltaY: number;
  deltaMode: number;
}

/**
 * A wheel event's vertical delta, in pixels.
 *
 * `deltaY` is only a pixel count when `deltaMode` says so. A browser may report
 * lines (Firefox's default) or pages instead, and taking the raw number then
 * moves the page by a couple of pixels per notch.
 */
function wheelDeltaPx(event: IWheelDelta, viewportHeight: number): number {
  if (event.deltaMode === DELTA_MODE_LINE) {
    return event.deltaY * WHEEL_LINE_HEIGHT_PX;
  }
  if (event.deltaMode === DELTA_MODE_PAGE) {
    return event.deltaY * viewportHeight;
  }
  return event.deltaY;
}

/**
 * Scrolls `container` by one wheel event's worth of travel.
 *
 * A no-op for a missing container or an event with no vertical delta, so a call
 * site can hand over a ref's current value without a guard of its own. The
 * browser clamps `scrollTop` to the scrollable range, so the two ends of the
 * scroll need no handling here.
 *
 * @param container - The scrolling element to move; `null` is tolerated.
 * @param event - The wheel event that was received.
 */
export function scrollByWheel(
  container: HTMLElement | null,
  event: IWheelDelta
): void {
  if (container === null || event.deltaY === 0) {
    return;
  }
  container.scrollTop += wheelDeltaPx(event, container.clientHeight);
}
